import React, { useState, useCallback } from 'react';
import { InlineSpinner } from '../components/LoadingState';
import LoadingState from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { FaFolder, FaCheckCircle, FaChevronDown, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { listJobs } from '../api/api';
import RefreshButton from '../components/RefreshButton';

const Jobs = ({ setAlert }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load initial jobs
  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listJobs();
      setJobs(data.jobs || []);
      setNextToken(data.nextToken || null);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more jobs
  const loadMore = useCallback(async () => {
    if (!nextToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await listJobs({ nextToken });
      setJobs(prev => [...prev, ...(data.jobs || [])]);
      setNextToken(data.nextToken || null);
    } catch (err) {
      console.error('Error loading more jobs:', err);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load more jobs.'
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextToken, isLoadingMore, setAlert]);

  // Load jobs on mount
  React.useEffect(() => {
    if (!hasLoaded) {
      loadJobs();
    }
  }, [hasLoaded, loadJobs]);

  // Refresh function
  const mutate = useCallback(() => {
    loadJobs();
  }, [loadJobs]);

  // Reads operationStatus (has the backend finished?), not status (have the QA
  // assessors finished?). Those are independent, and showing the review counter
  // here is why every job appeared to be "Processing" forever.
  const getStatusBadge = (operationStatus) => {
    switch (operationStatus) {
      case 'Complete':
        return (
          <span className="status-badge status-pass">
            <FaCheckCircle />
            Complete
          </span>
        );
      case 'Processing':
        return (
          <span className="status-badge !bg-cloudza-100 !text-cloudza-800">
            <FaClock />
            Processing
          </span>
        );
      case 'CompletedWithFailures':
        return (
          <span className="status-badge status-fail">
            <FaExclamationTriangle />
            Completed with failures
          </span>
        );
      default:
        return (
          <span className="status-badge status-not-started">
            {operationStatus}
          </span>
        );
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  if (isLoading) {
    return (
      <LoadingState variant="stats" label="Loading jobs" />
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50">
        <p className="text-red-700">Failed to load jobs. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Upload Jobs</h1>
          <p className="text-gray-500 text-sm mt-1">
            View and manage your upload job batches
          </p>
        </div>
        <RefreshButton onRefresh={mutate} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-tour="job-stats">
        {[
          { label: 'Total Jobs', value: jobs.length, icon: <FaFolder /> },
          // These two are review progress, so they stay on `status`.
          { label: 'Assessed', value: jobs.filter(j => j.status === 'Completed').length, icon: <FaCheckCircle /> },
          { label: 'Awaiting assessment', value: jobs.filter(j => j.status === 'Processing' || j.status === 'Partial').length, icon: <FaClock /> },
          // Pipeline failures, so this one reads operationStatus. It previously
          // counted status === 'Failed', which nothing ever set — it was always 0.
          { label: 'Failed', value: jobs.filter(j => j.operationStatus === 'CompletedWithFailures').length, icon: <FaExclamationTriangle /> },
        ].map(({ label, value, icon }) => (
          <div key={label} className="card-hover">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{value}</p>
              </div>
              <div className="flex-shrink-0 w-9 h-9 bg-gray-50 text-gray-400 rounded-xl flex items-center justify-center text-sm">
                {icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Jobs Table */}
      <div className="card">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Recent Jobs</h2>
        {jobs.length === 0 ? (
          <div className="text-center py-12">
            <FaFolder className="mx-auto text-3xl text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-700">No jobs found</p>
            <p className="text-sm text-gray-400 mt-1">
              Upload some recordings to see your jobs here
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Job ID
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Upload Date
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Campaign
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Template
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Uploaded By
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Assessed
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Status
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {jobs.map((job) => (
                  <tr
                    key={job.jobId}
                    className="hover:bg-cloudza-50/40 transition-colors cursor-pointer"
                    onClick={() => handleJobClick(job.jobId)}
                  >
                    <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {job.jobId === 'legacy' ? 'Legacy' : job.jobId.substring(0, 20) + '...'}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(job.uploadDate)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.campaign}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.template}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                      {job.uploadedBy}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                      {job.completedCount}/{job.fileCount}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {getStatusBadge(job.operationStatus)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-cyan-600 hover:text-cyan-800">
                      View Details →
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Load More Button */}
            {nextToken && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? (
                    <>
                      <InlineSpinner light className="mr-2" />
                      Loading...
                    </>
                  ) : (
                    <><FaChevronDown /> Load More Jobs</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Jobs;
